import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@staff-app/ui';
import { ADMIN_ROLES, type AdminRole, type AdminUser } from '@staff-app/shared-types';
import { STAFF_INVITE_ADMIN_USER } from '@staff-app/graphql-client';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface InviteResult {
  staffInviteAdminUser: AdminUser;
}

const ROLE_OPTIONS: { value: AdminRole; label: string; description: string }[] = [
  { value: ADMIN_ROLES.ROOT, label: 'Root', description: 'Full access' },
  { value: ADMIN_ROLES.ENGINEER, label: 'Engineer', description: 'Network + My Account' },
  { value: ADMIN_ROLES.SUPPORT, label: 'Support', description: 'Customers + Network + My Account' },
  {
    value: ADMIN_ROLES.FULFILLMENT,
    label: 'Fulfillment',
    // Says what it grants AND what it does not: this role issues spendable Shopify codes with no
    // invoice behind them, so nobody should be handed it under the impression it is a mild
    // read-only tier. It is narrow, not weak.
    description: 'Devices Fulfillment only — issues demo device coupons',
  },
  {
    value: ADMIN_ROLES.BILLING,
    label: 'Billing',
    // Narrow, like Fulfillment: it can switch a customer's screens off from the review queue.
    description: 'Dunning Review only — review queue, suspended list, exclusions',
  },
  { value: ADMIN_ROLES.READONLY, label: 'Read only', description: 'My Account only' },
];

const DEFAULT_ROLES: AdminRole[] = [ADMIN_ROLES.ENGINEER];

export function InviteUserDialog({ open, onOpenChange }: Props) {
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<AdminRole[]>(DEFAULT_ROLES);

  const [inviteMutation, { loading }] = useMutation<InviteResult>(STAFF_INVITE_ADMIN_USER, {
    refetchQueries: ['staffAdminUsers'],
  });

  const emailOk = /.+@optisigns\.com$/i.test(email.trim());
  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: loading,
    isRequiredFilled: !!email.trim() && roles.length > 0,
    validationError: email.trim() && !emailOk ? 'Must be an @optisigns.com address' : '',
  });

  const toggleRole = (role: AdminRole) => {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const handleSubmit = async () => {
    try {
      await inviteMutation({
        variables: { email: email.trim(), roles: roles.map((r) => r.toUpperCase() as AdminRole) },
      });
      toast.success('Invite sent', {
        description: 'User will have access on their next @optisigns.com sign-in.',
      });
      setEmail('');
      setRoles(DEFAULT_ROLES);
      onOpenChange(false);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Unknown error';
      if (/already a staff user|Already invited|E11000|duplicate key/i.test(msg)) {
        const rolesMatch = /\(roles?: ([\w, ]+)\)/.exec(msg);
        toast.error('User already exists', {
          description: `${email.trim().toLowerCase()} is already a staff user${
            rolesMatch ? ` with roles "${rolesMatch[1]}"` : ''
          }. Find them in the list and use "Change roles" instead.`,
        });
        return;
      }
      toast.error('Invite failed', { description: msg });
    }
  };

  const resetOnClose = (next: boolean) => {
    if (!next) {
      setEmail('');
      setRoles(DEFAULT_ROLES);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={resetOnClose}>
      <DialogContent data-testid="invite-user-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{'Invite user'}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="invite-email">{'Email'}</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="first.last@optisigns.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="invite-email-input"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label>{'Roles'}</Label>
            <div className="grid grid-cols-2 gap-2">
              {ROLE_OPTIONS.map(({ value, label, description }) => {
                const id = `invite-role-${value}`;
                return (
                  <div
                    key={value}
                    className="flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer"
                    onClick={() => toggleRole(value)}
                  >
                    <Checkbox
                      id={id}
                      checked={roles.includes(value)}
                      onCheckedChange={() => toggleRole(value)}
                    />
                    <div className="flex flex-col">
                      <Label htmlFor={id} className="cursor-pointer font-medium">
                        {label}
                      </Label>
                      <span className="text-xs text-muted-foreground">{description}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            {'They will get access on first Google sign-in.'}
          </p>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{'Cancel'}</Button>
          </DialogClose>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={submitDisabled ? 0 : -1}>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    data-testid="invite-user-submit"
                  >
                    {loading ? 'Sending…' : 'Send invite'}
                  </Button>
                </span>
              </TooltipTrigger>
              {disabledTooltip ? <TooltipContent>{disabledTooltip}</TooltipContent> : null}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
